import { type ReactElement, useId } from 'react';
import { useTranslation } from '../i18n/useLanguage.js';
import { Button } from './Button.js';

export interface FilterInputProps {
  readonly label: string;
  readonly placeholder: string;
  readonly value: string;
  readonly matchCount: number;
  readonly totalCount: number;
  onChange(next: string): void;
}

/**
 * The filter box above a list.
 *
 * The result count is announced politely rather than shouted, so a screen
 * reader user hears "3 of 12 shown" after they stop typing instead of on every
 * keystroke.
 */
export function FilterInput({
  label,
  placeholder,
  value,
  matchCount,
  totalCount,
  onChange,
}: FilterInputProps): ReactElement {
  const t = useTranslation();
  const id = useId();
  const isFiltering = value.trim() !== '';

  return (
    <div className="filter-bar">
      <label className="visually-hidden" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="search"
        className="input filter-bar__input"
        placeholder={placeholder}
        value={value}
        autoComplete="off"
        onChange={(event) => onChange(event.target.value)}
      />
      {isFiltering ? (
        <>
          <span className="filter-bar__count" role="status" aria-live="polite">
            {t.filterInput.matchOf(matchCount, totalCount)}
          </span>
          <Button variant="ghost" small onClick={() => onChange('')}>
            {t.filterInput.clear}
          </Button>
        </>
      ) : (
        <span className="filter-bar__count">{totalCount}</span>
      )}
    </div>
  );
}
